import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { createContext, useContext, useEffect, useRef, useState, type ComponentProps, type ReactNode } from 'react';

export type MarkdownTextLanguage = 'tr' | 'en';

export interface MarkdownTextProps {
  children?: string | null;
  className?: string;
  attachments?: MarkdownAttachmentPresentation[];
  /** Manual recovery after a real failure (denied, unavailable, media that could not be shown). */
  onAttachmentRetry?: (id: string) => void;
  /**
   * Asks for a fresh URL for a link whose lease ended without a failure (status `stale`). It is
   * called when the reader presses, focuses or activates the link (never on a resting pointer); the link opens as soon as the
   * new URL is ready after an activation. Falls back to `onAttachmentRetry` when absent.
   */
  onAttachmentRefresh?: (id: string) => void;
  /** A displayed image finished loading with this source; it is then kept as rendered. */
  onAttachmentLoad?: (id: string, src: string | null | undefined) => void;
  /**
   * The <img> that reported `onAttachmentLoad` is gone (unmounted, or replaced by an alert). The kept source
   * belonged to that element, so the owner releases it and a newly mounted image starts from a valid URL.
   */
  onAttachmentUnload?: (id: string) => void;
  /** A displayed image or video could not be loaded from its URL; the owner can stop refreshing it until a manual retry. */
  onAttachmentError?: (id: string) => void;
  /** Copy of the recovery UI (retry, loading, unavailable, media failure). Defaults to Turkish. */
  language?: MarkdownTextLanguage;
}

export interface MarkdownAttachmentPresentation {
  id: string;
  /**
   * Opaque identity of the signed file behind this presentation (signing scope, id, digest, version).
   * When it changes for the same `id` (another document version, another scope), everything the renderer
   * remembers about that id (failed media, blocked or pending links) is forgotten.
   */
  identity?: string;
  filename: string;
  mimeType: string;
  kind: 'image' | 'video' | 'document';
  /** The URL a link or a download may use: only ever a URL whose lease is valid. */
  viewUrl?: string | null;
  /**
   * Present while a mounted <img> of this attachment has loaded and may stay on screen as rendered after
   * its lease ended (a loaded image is never downloaded again). It is a marker, not a source: the
   * <img> element remembers the source it loaded with itself, and an element mounted later always starts
   * from `viewUrl`. Links follow `viewUrl`/`status`, so a link to an image that is also displayed never
   * receives an expired URL.
   */
  renderedSrc?: string | null;
  /**
   * `stale`: the lease of a link ended without a failure; neutral, renewed when the link is used.
   * `expired`/`error`: a real failure that offers a manual retry.
   */
  status?: 'ready' | 'loading' | 'error' | 'expired' | 'stale';
  errorMessage?: string | null;
}

const baseClass =
  'text-sm leading-relaxed text-slate-200 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_a]:text-brand-300 [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:text-brand-200 [&_blockquote]:border-l-2 [&_blockquote]:border-slate-600 [&_blockquote]:pl-3 [&_blockquote]:text-slate-300 [&_code]:rounded [&_code]:bg-slate-950/70 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-[0.9em] [&_code]:text-slate-100 [&_li]:my-1 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-3 [&_pre]:my-3 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:border [&_pre]:border-white/5 [&_pre]:bg-slate-950/80 [&_pre]:p-3 [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_strong]:font-semibold [&_strong]:text-white [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-5';

const copy = {
  tr: {
    unavailable: 'Ek kullanılamıyor',
    preparing: (name: string) => `${name} hazırlanıyor…`,
    openFailed: (name: string) => `${name} açılamadı.`,
    imageFailed: (name: string) => `${name} görüntülenemedi.`,
    mediaFailed: (name: string) => `${name} oynatılamadı veya görüntülenemedi.`,
    retry: 'Yeniden dene',
    refresh: 'Yenile',
    linkReady: 'Bağlantı hazır, açmak için tıklayın',
    remoteBlocked: 'Uzak görsel gösterimine izin verilmiyor.',
    videoUnsupported: 'Video oynatılamıyor.',
    openFile: 'Dosyayı açın',
    documentAsLink: 'Belgeyi indirmek için bağlantı biçimini kullanın.',
  },
  en: {
    unavailable: 'Attachment unavailable',
    preparing: (name: string) => `Preparing ${name}…`,
    openFailed: (name: string) => `${name} could not be opened.`,
    imageFailed: (name: string) => `${name} could not be displayed.`,
    mediaFailed: (name: string) => `${name} could not be played or displayed.`,
    retry: 'Try again',
    refresh: 'Refresh',
    linkReady: 'Link ready, click to open',
    remoteBlocked: 'Remote images are not allowed.',
    videoUnsupported: 'The video cannot be played.',
    openFile: 'Open the file',
    documentAsLink: 'Use link syntax to download a document.',
  },
} as const;

const attachmentId = (href?: string) => href?.startsWith('attachment:') ? href.slice('attachment:'.length) : null;

/**
 * What the Markdown elements need to render an attachment. The element components below are
 * module-level and read this context, so their identity never changes between renders: a lease
 * tick, a tab return or a status change re-renders them in place instead of remounting every
 * image and video (which would restart playback and download an image again).
 */
interface MediaView {
  attachmentMode: boolean;
  attachments: MarkdownAttachmentPresentation[];
  text: (typeof copy)[MarkdownTextLanguage];
  failedMedia: ReadonlySet<string>;
  markFailed: (id: string) => void;
  /** Links whose renewed URL the browser refused to open from the effect (no user activation left). */
  blockedLinks: ReadonlySet<string>;
  clearBlocked: (id: string) => void;
  clearFailed: (id: string) => void;
  /** Stale links being renewed stay links (not a status line), so the click that follows lands on them. */
  renewing: Set<string>;
  /** Links the reader activated while their URL was being renewed: opened once the URL is ready. */
  pendingOpen: Set<string>;
  retry?: (id: string) => void;
  renew?: (id: string) => void;
  onLoad?: (id: string, src: string | null | undefined) => void;
  onUnload?: (id: string) => void;
}

const MediaContext = createContext<MediaView | null>(null);
const useMedia = () => {
  const view = useContext(MediaContext);
  if (!view) throw new Error('MarkdownText element rendered outside MarkdownText');
  return view;
};

function MarkdownLink({ href, children, node: _node, ...props }: ComponentProps<'a'> & { node?: unknown }) {
  const { attachments, text, renewing, pendingOpen, blockedLinks, clearBlocked, retry, renew } = useMedia();
  const id = attachmentId(href);
  const blocked = id !== null && blockedLinks.has(id);
  const anchorRef = useRef<HTMLAnchorElement>(null);
  // The renewed URL could not be opened on its own (popup blocked): focus the now-ready link so Enter or a click opens it.
  useEffect(() => { if (blocked) anchorRef.current?.focus(); }, [blocked]);
  if (!id) return <a href={href} target="_blank" rel="noreferrer" {...props}>{children}</a>;
  const asset = attachments.find((item) => item.id === id);
  if (!asset) return <span role="alert" className="text-red-400">{text.unavailable}</span>;
  // Every live state of a link is an <a> at the same position of the same fragment. A thumbnail
  // (`[![alt](attachment:a)](attachment:a)`) is the child of this link, and an element of another type
  // would remount the <img> when the lease ends or the URL is renewed, downloading it again from a
  // URL that may have expired.
  if (asset.status === 'stale' || (asset.status === 'loading' && renewing.has(id))) {
    const warm = () => { if (asset.status === 'stale') { renewing.add(id); renew?.(id); } };
    const activate = () => { clearBlocked(id); pendingOpen.add(id); warm(); renewing.add(id); };
    return (
      <>
        <a
          role="button"
          tabIndex={0}
          className="cursor-pointer text-brand-300 underline underline-offset-2 hover:text-brand-200"
          aria-busy={asset.status === 'loading'}
          onPointerDown={warm}
          onFocus={warm}
          onClick={activate}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); activate(); }
          }}
        >{children}</a>
        {null}
      </>
    );
  }
  if (asset.status === 'loading') return <span role="status" className="text-slate-400">{text.preparing(asset.filename)}</span>;
  if (!asset.viewUrl || asset.status === 'error' || asset.status === 'expired') return (
    <span role="alert" className="inline-flex items-center gap-2 text-red-400">
      {asset.errorMessage || text.openFailed(asset.filename)}
      {retry && <button type="button" className="underline" onClick={() => retry(id)}>{text.retry}</button>}
    </span>
  );
  return (
    <>
      <a ref={anchorRef} href={asset.viewUrl} target="_blank" rel="noreferrer" download={asset.kind === 'document' ? asset.filename : undefined} onClick={blocked ? () => clearBlocked(id) : undefined} {...props}>{children}</a>
      {blocked ? <span role="status" className="ml-2 text-xs text-slate-400">{text.linkReady}</span> : null}
    </>
  );
}

/**
 * A <video> does not reload when the `src` of its <source> changes: it keeps `currentSrc`, so a
 * seek or rebuffer after the first lease would still request the expired URL. When the signed URL is
 * renewed for a mounted player, the element is reloaded with the new source and puts the playhead,
 * the paused state and the speed back, so the next range request uses a valid URL.
 *
 * A player that was never started (paused at the very beginning) is not reloaded on every renewal: that
 * would fetch its metadata again each lease period for nothing. It is reloaded at the moment it is
 * played or seeked instead, which is when a request with the stale `currentSrc` would be made.
 */
function RenewableVideo({ src, mimeType, label, onFailed, unsupported, openFile }: {
  src: string; mimeType: string; label: string; onFailed: () => void; unsupported: string; openFile: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const loadedSrc = useRef(src);
  /** The element still holds the URL it was loaded with; the renewed one is applied on demand. */
  const outdated = useRef(false);
  const stopRestore = useRef<(() => void) | null>(null);
  const reload = (video: HTMLVideoElement, resumePlayback?: boolean) => {
    outdated.current = false;
    stopRestore.current?.();
    const position = video.currentTime;
    const resume = resumePlayback ?? (!video.paused && !video.ended);
    const rate = video.playbackRate;
    const restore = () => {
      try {
        if (position > 0) video.currentTime = position;
        video.playbackRate = rate;
      } catch { /* the element is already gone or not seekable: the player starts from the beginning */ }
      if (resume) void video.play?.()?.catch(() => undefined);
    };
    video.addEventListener('loadedmetadata', restore, { once: true });
    stopRestore.current = () => video.removeEventListener('loadedmetadata', restore);
    video.load();
  };
  useEffect(() => {
    const video = ref.current;
    if (!video || loadedSrc.current === src) return;
    loadedSrc.current = src;
    const started = video.currentTime > 0 || !video.paused || video.ended;
    if (started) reload(video);
    else outdated.current = true;
  }, [src]);
  useEffect(() => {
    const video = ref.current;
    if (!video) return undefined;
    const onPlay = () => { if (outdated.current) reload(video, true); };
    const onSeeking = () => { if (outdated.current) reload(video); };
    video.addEventListener('play', onPlay);
    video.addEventListener('seeking', onSeeking);
    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('seeking', onSeeking);
      stopRestore.current?.();
    };
  }, []);
  return (
    <video ref={ref} controls preload="metadata" playsInline aria-label={label} onError={onFailed} className="my-4 max-h-[34rem] w-full rounded-lg border border-slate-700 bg-slate-950">
      {/* A rejected <source> reports on the source element, not on the video. */}
      <source src={src} type={mimeType} onError={(event) => { event.stopPropagation(); onFailed(); }} />
      {unsupported} <a href={src}>{openFile}</a>.
    </video>
  );
}

/**
 * The <img> of an attachment. The source a loaded picture is kept on (after its lease ended) belongs to
 * THIS element: it is remembered here, never in the owner, so an element that is mounted later (a preview
 * shown again, an editor that re-parses its text) starts from the URL that is valid now. The owner is told
 * when the element that loaded goes away.
 */
function AttachmentImage({ asset, alt, keep, fallback }: { asset: MarkdownAttachmentPresentation; alt: string; keep: boolean; fallback: ReactNode }) {
  const { markFailed, onLoad, onUnload } = useMedia();
  const identity = asset.identity ?? '';
  const [loaded, setLoaded] = useState<{ id: string; identity: string; src: string } | null>(null);
  const onUnloadRef = useRef(onUnload);
  onUnloadRef.current = onUnload;
  const loadedId = loaded?.id;
  useEffect(() => (loadedId ? () => onUnloadRef.current?.(loadedId) : undefined), [loadedId]);
  const kept = keep && loaded && loaded.id === asset.id && loaded.identity === identity ? loaded.src : undefined;
  const src = kept ?? asset.viewUrl;
  if (!src) return <>{fallback}</>;
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onLoad={() => { setLoaded({ id: asset.id, identity, src }); onLoad?.(asset.id, src); }}
      onError={() => markFailed(asset.id)}
      className="my-4 max-h-[42rem] w-auto max-w-full rounded-lg border border-slate-700 bg-slate-950 object-contain"
    />
  );
}

function MarkdownImage({ src, alt }: ComponentProps<'img'> & { node?: unknown }) {
  const { attachmentMode, attachments, text, failedMedia, markFailed, clearFailed, retry, renew } = useMedia();
  const source = typeof src === 'string' ? src : undefined;
  const id = attachmentId(source);
  if (!id) return attachmentMode ? <span role="alert" className="text-red-400">{text.remoteBlocked}</span> : <img src={source} alt={alt || ''} loading="lazy" />;
  const asset = attachments.find((item) => item.id === id);
  if (!asset) return <span role="alert" className="text-red-400">{text.unavailable}</span>;
  // A loaded image stays on screen with the source its element was rendered with, whatever the lease of
  // the link URL does; only a failure (reported through `renderedSrc`/`failedMedia`) replaces it.
  const keepImage = asset.kind === 'image' && Boolean(asset.renderedSrc) && !failedMedia.has(id);
  const unavailable = ((): ReactNode => {
    if (asset.status === 'loading') return <span role="status" className="text-slate-400">{text.preparing(asset.filename)}</span>;
    if (asset.status === 'stale') return (
      <span role="status" className="block text-slate-400">
        {asset.filename}
        {renew && <button type="button" className="ml-2 underline" onClick={() => renew(id)}>{text.refresh}</button>}
      </span>
    );
    if (!asset.viewUrl || asset.status === 'error' || asset.status === 'expired' || failedMedia.has(id)) return (
      <span role="alert" className="block rounded-md border border-red-500/20 bg-red-500/10 p-3 text-red-400">
        {failedMedia.has(id) ? text.mediaFailed(asset.filename) : asset.errorMessage || text.imageFailed(asset.filename)}
        {retry && <button type="button" className="ml-2 underline" onClick={() => { clearFailed(id); retry(id); }}>{text.retry}</button>}
      </span>
    );
    return null;
  })();
  if (!keepImage && unavailable) return unavailable;
  if (asset.kind === 'video') return (
    <RenewableVideo src={asset.viewUrl!} mimeType={asset.mimeType} label={alt || asset.filename} onFailed={() => markFailed(id)} unsupported={text.videoUnsupported} openFile={text.openFile} />
  );
  if (asset.kind === 'document') return <span role="alert" className="text-red-400">{text.documentAsLink}</span>;
  return <AttachmentImage asset={asset} alt={alt || asset.filename} keep={keepImage} fallback={unavailable ?? <span role="status" className="text-slate-400">{text.preparing(asset.filename)}</span>} />;
}

const markdownComponents = {
  a: MarkdownLink,
  img: MarkdownImage,
  input: ({ node: _node, ...props }: ComponentProps<'input'> & { node?: unknown }) => <input {...props} disabled className="mr-2 align-middle" />,
};

export function MarkdownText({ children, className = '', attachments, onAttachmentRetry, onAttachmentRefresh, onAttachmentLoad, onAttachmentUnload, onAttachmentError, language = 'tr' }: MarkdownTextProps) {
  const [failedMedia, setFailedMedia] = useState<ReadonlySet<string>>(() => new Set());
  const [blockedLinks, setBlockedLinks] = useState<ReadonlySet<string>>(() => new Set());
  const pendingOpen = useRef(new Set<string>());
  const renewing = useRef(new Set<string>());
  // What is remembered per attachment id (failed media, blocked links, links waiting to open) belongs to
  // the file behind that id at the time. When the same id comes back under another identity (another
  // document version or signing scope shown without remounting this component), or goes away, it is
  // forgotten, so a failure of version A is never shown for version B.
  const identities = attachments?.map((item) => `${item.id}\u0000${item.identity ?? ''}`).join('\n') ?? '';
  const [seenIdentities, setSeenIdentities] = useState(identities);
  if (seenIdentities !== identities) {
    const before = new Map(seenIdentities.split('\n').filter(Boolean).map((entry) => entry.split('\u0000') as [string, string]));
    const now = new Map(identities.split('\n').filter(Boolean).map((entry) => entry.split('\u0000') as [string, string]));
    const forgotten = [...before].filter(([id, identity]) => now.get(id) !== identity).map(([id]) => id);
    setSeenIdentities(identities);
    if (forgotten.length) {
      for (const id of forgotten) { renewing.current.delete(id); pendingOpen.current.delete(id); }
      const without = (current: ReadonlySet<string>) => (forgotten.some((id) => current.has(id)) ? new Set([...current].filter((id) => !forgotten.includes(id))) : current);
      setFailedMedia(without);
      setBlockedLinks(without);
    }
  }
  useEffect(() => {
    for (const id of [...renewing.current]) {
      const asset = attachments?.find((item) => item.id === id);
      if (asset?.status === 'loading' || asset?.status === 'stale') continue;
      renewing.current.delete(id);
      if (!pendingOpen.current.delete(id)) continue;
      if (asset?.status !== 'ready' || !asset.viewUrl) continue;
      // `noopener` would make the call return null whether or not a popup opened, hiding a block. The
      // opener is cut right after instead, so a blocked popup (no user activation left after the
      // signing round trip: Safari, Chrome after about 5 s) is noticed and the reader is told.
      const opened = window.open(asset.viewUrl, '_blank');
      if (opened) {
        try { opened.opener = null; } catch { /* a cross-origin window cannot be reached; it has no opener anyway */ }
      } else {
        const blockedId = id;
        setBlockedLinks((current) => (current.has(blockedId) ? current : new Set(current).add(blockedId)));
      }
    }
  }, [attachments]);
  const source = children?.trim();
  if (!source) return null;

  const view: MediaView = {
    attachmentMode: attachments !== undefined,
    attachments: attachments ?? [],
    text: copy[language],
    failedMedia,
    markFailed: (id) => {
      if (failedMedia.has(id)) return;
      setFailedMedia((current) => (current.has(id) ? current : new Set(current).add(id)));
      onAttachmentError?.(id);
    },
    blockedLinks,
    clearBlocked: (id) => setBlockedLinks((current) => { if (!current.has(id)) return current; const next = new Set(current); next.delete(id); return next; }),
    clearFailed: (id) => setFailedMedia((current) => { const next = new Set(current); next.delete(id); return next; }),
    renewing: renewing.current,
    pendingOpen: pendingOpen.current,
    retry: onAttachmentRetry,
    renew: onAttachmentRefresh ?? onAttachmentRetry,
    onLoad: onAttachmentLoad,
    onUnload: onAttachmentUnload,
  };

  return (
    <div className={`${baseClass} ${className}`.trim()}>
      <MediaContext.Provider value={view}>
        <ReactMarkdown
          skipHtml
          remarkPlugins={[remarkGfm]}
          urlTransform={(url) => url.startsWith('attachment:') ? url : /^https?:\/\//i.test(url) ? url : ''}
          components={markdownComponents}
        >
          {source}
        </ReactMarkdown>
      </MediaContext.Provider>
    </div>
  );
}
